import type { ScanRow } from './scanOrder';

/**
 * Row/column scanning, as a pure reducer (3.3).
 *
 * Same shape and the same reason as `dwellMachine.ts`: this is timing, and timing asserted through a browser is
 * asserted through a scheduler nobody controls. Everything the person can experience — how many taps a
 * selection costs, whether a wrong row traps them, when the highlight gives up — is decided here and testable
 * here.
 *
 * ── One switch, two levels ──
 *
 * The highlight advances by row on a timer. A press descends into that row and the highlight then advances by
 * column. A second press activates. That is the standard shape, and it is what makes a 33-key keyboard
 * reachable in about eleven steps instead of thirty-three.
 *
 * ── Loop-out at both levels, and why the column one matters more ──
 *
 * After `loops` passes with no press, row scanning returns to rest: a screen blinking at somebody who has
 * stopped is noise, and its stopping also tells a caregiver across the room that nothing is happening.
 *
 * The column loop-out is the one that protects the person. Without it, descending into the wrong row leaves
 * them trapped inside it with no way back — and for somebody whose only input is a single switch, being trapped
 * is far worse than being slow.
 */

export type ScanState =
  /** No highlight. Either nothing has started, or the cycle gave up. */
  | { kind: 'rest' }
  | { kind: 'rows'; index: number; loops: number }
  | { kind: 'columns'; rowIndex: number; index: number; loops: number };

export type ScanEvent =
  /** The interval fired. */
  | { kind: 'tick' }
  /** A tap anywhere, or Space/Enter from a switch. */
  | { kind: 'press' }
  /** The surface changed, or the targets did. Start again from the top of the cycle. */
  | { kind: 'reset' };

export interface ScanEffect {
  kind: 'activate';
  targetId: string;
  /**
   * How many steps this selection cost, for diagnostics.
   *
   * 3.3 asks to "measure scan cycles per selection and feed it to diagnostics", and this is the number that
   * makes `scanIntervalMs` tunable from evidence rather than from feel — a rising cost means the interval is
   * wrong for the person today.
   */
  steps: number;
}

export interface ScanConfig {
  /** Passes with no press before giving up. 3.3's "configurable loop count before returning to rest". */
  loops: number;
}

export interface ScanResult {
  state: ScanState;
  effect: ScanEffect | null;
}

export const restingScan = (): ScanState => ({ kind: 'rest' });

/** The first step of a cycle: row 0, which is STOP when the persistent bar is on screen. */
export const startScan = (): ScanState => ({ kind: 'rows', index: 0, loops: 0 });

export function reduceScan(
  state: ScanState,
  event: ScanEvent,
  rows: readonly ScanRow[],
  config: ScanConfig,
  /** Steps taken since the cycle began, counted by the adapter. */
  steps = 0,
): ScanResult {
  if (rows.length === 0) return { state: restingScan(), effect: null };

  if (event.kind === 'reset') {
    return { state: startScan(), effect: null };
  }

  switch (state.kind) {
    case 'rest': {
      // A press wakes it up rather than selecting anything. Waking and selecting with the same action would
      // mean the first tap after a pause activated whatever the highlight had been left on.
      if (event.kind === 'press') return { state: startScan(), effect: null };
      return { state, effect: null };
    }

    case 'rows': {
      if (event.kind === 'press') {
        const row = rows[state.index];
        if (!row || row.length === 0) return { state, effect: null };

        // A one-item row needs no column pass — which is what makes STOP a single press (C59).
        if (row.length === 1) {
          return { state: startScan(), effect: { kind: 'activate', targetId: row[0]!.id, steps } };
        }
        return { state: { kind: 'columns', rowIndex: state.index, index: 0, loops: 0 }, effect: null };
      }

      const next = state.index + 1;
      if (next < rows.length) return { state: { kind: 'rows', index: next, loops: state.loops }, effect: null };

      // Wrapped. One more pass, unless the allowance is spent.
      const loops = state.loops + 1;
      if (loops >= config.loops) return { state: restingScan(), effect: null };
      return { state: { kind: 'rows', index: 0, loops }, effect: null };
    }

    case 'columns': {
      const row = rows[state.rowIndex];
      // The targets changed under us — a candidate appeared, or the surface moved on. Rather than activate
      // something the person did not see highlighted, start the cycle again.
      if (!row || row.length === 0) return { state: startScan(), effect: null };

      if (event.kind === 'press') {
        const target = row[state.index];
        if (!target) return { state: startScan(), effect: null };
        return { state: startScan(), effect: { kind: 'activate', targetId: target.id, steps } };
      }

      const next = state.index + 1;
      if (next < row.length) {
        return { state: { kind: 'columns', rowIndex: state.rowIndex, index: next, loops: state.loops }, effect: null };
      }

      // End of the row. Another pass, or back out to row level — the escape from a wrongly chosen row.
      const loops = state.loops + 1;
      if (loops >= config.loops) {
        return { state: { kind: 'rows', index: state.rowIndex, loops: 0 }, effect: null };
      }
      return { state: { kind: 'columns', rowIndex: state.rowIndex, index: 0, loops }, effect: null };
    }
  }
}

/** Which target ids are highlighted right now: a whole row, or one target within it. */
export function highlighted(state: ScanState, rows: readonly ScanRow[]): readonly string[] {
  switch (state.kind) {
    case 'rest':
      return [];
    case 'rows':
      return (rows[state.index] ?? []).map((target) => target.id);
    case 'columns': {
      const target = rows[state.rowIndex]?.[state.index];
      return target ? [target.id] : [];
    }
  }
}
